import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Download, X, Share, ChevronLeft, ChevronRight, WifiOff, Zap, Scissors, Box } from 'lucide-react';
import { useIsMobile } from '../hooks/useMediaQuery';

const DISMISS_KEY = 'pwa-install-dismissed';
const DISMISS_DURATION = 7 * 24 * 60 * 60 * 1000; // 7 days

const SLIDES = [
  {
    image: '/example/cutcalc-parts-list.png',
    title: 'Optimize Your Cut Lists',
    description: 'Enter your parts and sheet sizes — get optimal cutting layouts instantly with minimal waste.',
  },
  {
    image: '/example/cutcalc-sheet-layout.png',
    title: 'Detailed Cut Diagrams',
    description: 'Color-coded layouts with exact dimensions, grain direction, and part labels for every sheet.',
  },
  {
    image: '/example/cutcalc-3d-import.png',
    title: 'Import 3D Models',
    description: 'Drag in SketchUp, Fusion 360, or STL files — parts are extracted automatically.',
  },
  {
    image: '/example/cutcalc-material-usage.png',
    title: 'Shop Sheet & Cut Checklist',
    description: 'Print a shop sheet with interactive cut tracking — check off pieces as you go.',
  },
];

function isStandalone() {
  return window.matchMedia('(display-mode: standalone)').matches
    || window.navigator.standalone === true;
}

function isIOS() {
  return /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
}

export default function PWAInstallPrompt() {
  const isMobile = useIsMobile();
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [showBanner, setShowBanner] = useState(false);
  const [showIOSGuide, setShowIOSGuide] = useState(false);
  const [slide, setSlide] = useState(0);
  const touchStartX = useRef(null);
  const totalSlides = SLIDES.length + 1; // feature slides + install slide
  const isLastSlide = slide === totalSlides - 1;

  useEffect(() => {
    // Only show PWA install prompt on mobile devices
    if (!isMobile) return;
    if (isStandalone()) return;

    const dismissed = localStorage.getItem(DISMISS_KEY);
    if (dismissed && Date.now() - parseInt(dismissed) < DISMISS_DURATION) return;

    const handler = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      setShowBanner(true);
    };
    window.addEventListener('beforeinstallprompt', handler);

    if (isIOS() && !('beforeinstallprompt' in window)) {
      const timer = setTimeout(() => setShowBanner(true), 2000);
      return () => {
        clearTimeout(timer);
        window.removeEventListener('beforeinstallprompt', handler);
      };
    }

    return () => window.removeEventListener('beforeinstallprompt', handler);
  }, [isMobile]);

  const handleInstall = useCallback(async () => {
    if (deferredPrompt) {
      deferredPrompt.prompt();
      const { outcome } = await deferredPrompt.userChoice;
      if (outcome === 'accepted') {
        setShowBanner(false);
      }
      setDeferredPrompt(null);
    } else if (isIOS()) {
      setShowIOSGuide(true);
    }
  }, [deferredPrompt]);

  const handleDismiss = useCallback(() => {
    setShowBanner(false);
    setShowIOSGuide(false);
    localStorage.setItem(DISMISS_KEY, Date.now().toString());
  }, []);

  const goNext = () => setSlide((s) => Math.min(s + 1, totalSlides - 1));
  const goPrev = () => setSlide((s) => Math.max(s - 1, 0));

  const handleTouchStart = (e) => { touchStartX.current = e.touches[0].clientX; };
  const handleTouchEnd = (e) => {
    if (touchStartX.current === null) return;
    const dx = e.changedTouches[0].clientX - touchStartX.current;
    if (Math.abs(dx) > 50) {
      if (dx < 0) goNext();
      else goPrev();
    }
    touchStartX.current = null;
  };

  // Lock body scroll while modal is open
  useEffect(() => {
    if (showBanner || showIOSGuide) {
      document.body.style.overflow = 'hidden';
      return () => { document.body.style.overflow = ''; };
    }
  }, [showBanner, showIOSGuide]);

  if (!showBanner) return null;

  // iOS instructions modal
  if (showIOSGuide) {
    return (
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-2xl max-w-md w-full shadow-[0_20px_60px_-15px_rgba(0,0,0,0.4)] overflow-hidden">
          <div className="bg-[#E27C39] p-6 text-white relative">
            <button
              onClick={handleDismiss}
              className="absolute top-4 right-4 text-white/80 hover:text-white"
            >
              <X className="w-5 h-5" />
            </button>
            <div className="flex items-center gap-3 mb-2">
              <Download className="w-7 h-7" />
              <h2 className="text-xl font-bold">Install CutCalc</h2>
            </div>
            <p className="text-white/80 text-sm">Add to your home screen to use offline</p>
          </div>
          <div className="p-6">
            <ol className="space-y-4 mb-6">
              <li className="flex items-start gap-3">
                <div className="w-6 h-6 rounded-full bg-sage-100 flex items-center justify-center flex-shrink-0">
                  <span className="text-xs font-bold text-sage-700">1</span>
                </div>
                <span className="text-gray-700">
                  Tap the <Share className="w-4 h-4 inline text-blue-500 -mt-0.5" /> <strong>Share</strong> button in your browser's toolbar
                </span>
              </li>
              <li className="flex items-start gap-3">
                <div className="w-6 h-6 rounded-full bg-sage-100 flex items-center justify-center flex-shrink-0">
                  <span className="text-xs font-bold text-sage-700">2</span>
                </div>
                <span className="text-gray-700">
                  Scroll down and tap <strong>"Add to Home Screen"</strong>
                </span>
              </li>
              <li className="flex items-start gap-3">
                <div className="w-6 h-6 rounded-full bg-sage-100 flex items-center justify-center flex-shrink-0">
                  <span className="text-xs font-bold text-sage-700">3</span>
                </div>
                <span className="text-gray-700">
                  Tap <strong>"Add"</strong> — CutCalc will work offline, just like a native app
                </span>
              </li>
            </ol>
            <button
              onClick={handleDismiss}
              className="w-full py-2 text-gray-400 hover:text-gray-600 text-sm"
            >
              Dismiss
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Feature carousel + install modal
  const currentFeature = SLIDES[slide];

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div
        className="bg-white rounded-2xl max-w-md w-full shadow-[0_20px_60px_-15px_rgba(0,0,0,0.4)] overflow-hidden"
        onTouchStart={handleTouchStart}
        onTouchEnd={handleTouchEnd}
      >
        {/* Close button */}
        <button
          onClick={handleDismiss}
          className="absolute top-3 right-3 z-10 w-8 h-8 flex items-center justify-center rounded-full bg-black/30 text-white/80 hover:text-white hover:bg-black/50 transition-colors"
        >
          <X className="w-4 h-4" />
        </button>

        {isLastSlide ? (
          /* ── Install slide ── */
          <>
            <div className="bg-[#E27C39] p-6 text-white">
              <div className="flex items-center gap-3 mb-2">
                <Download className="w-8 h-8" />
                <h2 className="text-2xl font-bold">Install CutCalc</h2>
              </div>
              <p className="text-white/90">Use it anywhere — even without internet</p>
            </div>
            <div className="p-6">
              <ul className="space-y-3 mb-6">
                <li className="flex items-center gap-3">
                  <div className="w-5 h-5 rounded-full bg-sage-100 flex items-center justify-center flex-shrink-0">
                    <WifiOff className="w-3 h-3 text-sage-600" />
                  </div>
                  <span className="text-gray-700">Works offline — no internet needed</span>
                </li>
                <li className="flex items-center gap-3">
                  <div className="w-5 h-5 rounded-full bg-sage-100 flex items-center justify-center flex-shrink-0">
                    <Zap className="w-3 h-3 text-sage-600" />
                  </div>
                  <span className="text-gray-700">Launches instantly from your home screen</span>
                </li>
                <li className="flex items-center gap-3">
                  <div className="w-5 h-5 rounded-full bg-sage-100 flex items-center justify-center flex-shrink-0">
                    <Scissors className="w-3 h-3 text-sage-600" />
                  </div>
                  <span className="text-gray-700">Perfect for the garage or hardware store</span>
                </li>
              </ul>

              <button
                onClick={handleInstall}
                className="w-full py-3 bg-[#E27C39] hover:bg-[#d06a2a] text-white rounded-xl font-semibold flex items-center justify-center gap-2 transition-colors"
              >
                <Download className="w-5 h-5" />
                Install App
              </button>
              <button
                onClick={handleDismiss}
                className="w-full py-2 mt-3 text-gray-400 hover:text-gray-600 text-sm"
              >
                Maybe later
              </button>
            </div>
          </>
        ) : (
          /* ── Feature slide ── */
          <>
            <div className="relative bg-gray-100">
              <img
                src={currentFeature.image}
                alt={currentFeature.title}
                className="w-full object-contain"
              />
              <div className="absolute bottom-0 left-0 right-0 h-6 bg-gradient-to-t from-white to-transparent" />
            </div>
            <div className="px-6 pt-4 pb-2">
              <h3 className="text-lg font-bold text-gray-900 mb-1">{currentFeature.title}</h3>
              <p className="text-sm text-gray-600 leading-relaxed">{currentFeature.description}</p>
            </div>
          </>
        )}

        {/* Footer: dots + nav */}
        <div className="px-6 pb-5 flex items-center justify-between">
          {/* Prev button */}
          <button
            onClick={goPrev}
            disabled={slide === 0}
            className={`w-9 h-9 flex items-center justify-center rounded-lg transition-colors ${
              slide === 0
                ? 'text-gray-300 cursor-not-allowed'
                : 'text-gray-500 hover:bg-gray-100'
            }`}
          >
            <ChevronLeft className="w-5 h-5" />
          </button>

          {/* Dots */}
          <div className="flex gap-1.5">
            {Array.from({ length: totalSlides }).map((_, i) => (
              <button
                key={i}
                onClick={() => setSlide(i)}
                className={`rounded-full transition-all ${
                  i === slide
                    ? 'w-6 h-2 bg-[#E27C39]'
                    : 'w-2 h-2 bg-gray-300 hover:bg-gray-400'
                }`}
              />
            ))}
          </div>

          {/* Next button */}
          {isLastSlide ? (
            <div className="w-9" />
          ) : (
            <button
              onClick={goNext}
              className="w-9 h-9 flex items-center justify-center rounded-lg text-gray-500 hover:bg-gray-100 transition-colors"
            >
              <ChevronRight className="w-5 h-5" />
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
